import type { LaborProfile, Locale, LocalizedText } from './types';

export type CostFrequency = 'MONTHLY' | 'WEEKLY' | 'QUARTERLY' | 'YEARLY' | 'ONE_TIME';
export interface CostExpenseRow { id: string; name: string; suggestionId?: string; amountExact: string | null; frequency: CostFrequency; notes: string }
export interface CostExpenseSection { reviewed: boolean; rows: CostExpenseRow[] }
export interface CostLaborInput {
  reviewed: boolean; mode: 'UNSET' | 'MANUAL' | 'PROFILES'; amountExact: string | null;
  profiles: Array<{ profileId: string; hoursExact: string | null }>;
}
export interface CostBudgetInput {
  fixed: CostExpenseSection; labor: CostLaborInput; other: CostExpenseSection;
  forecast: {
    allocation: 'NONE' | 'PER_PORTION' | 'SALES_PERCENT';
    portionsExact: string | null; salesExact: string | null; targetMarginPercentExact: string | null;
  };
}
export interface CostLaborSnapshot {
  profileId: string; name: string; payType: 'HOURLY' | 'SALARY'; hoursExact: string | null;
  hourlyRateExact: string | null; annualSalaryExact: string | null; burdenPercentExact: string;
  monthlyCostExact: string | null;
}
export interface CostBudgetRecord {
  id: string; restaurantId: string; month: string; revision: number; input: CostBudgetInput;
  laborSnapshot: CostLaborSnapshot[]; createdAt: string | null; updatedAt: string | null; updatedByUid: string | null;
}
export interface CostBudgetSummary {
  fixedExact: string | null; laborExact: string | null; otherExact: string | null; totalExact: string | null;
  knownTotalExact: string; incompleteSections: Array<'FIXED' | 'LABOR' | 'OTHER'>;
  perPortionExact: string | null; salesRateExact: string | null;
}
export interface CostBudgetPayload {
  restaurantId: string; month: string; budget: CostBudgetRecord; summary: CostBudgetSummary;
  laborProfiles: LaborProfile[];
}
export interface MenuCostEstimateRequest { month: string; recipeId: string; sellingPriceExact?: string | null; portionEquivalentExact?: string | null }
export interface MenuCostEstimate {
  restaurantId: string; month: string; budgetRevision: number; recipeId: string; recipeName: LocalizedText;
  basis: 'PORTION' | 'YIELD_UNIT'; unit: string; allocation: CostBudgetInput['forecast']['allocation'];
  ingredientCostExact: string | null; sellingPriceExact: string | null;
  allocatedCostExact: string | null; totalCostExact: string | null; profitExact: string | null;
  marginPercentExact: string | null; grossMarginPercentExact: string | null;
  suggestedPriceExact: string | null; targetMarginPercentExact: string | null;
  issues: Array<'BUDGET_INCOMPLETE' | 'FORECAST_MISSING' | 'ALLOCATION_MISSING' | 'PORTION_EQUIVALENT_MISSING' | 'INGREDIENT_COST_MISSING' | 'PRICE_MISSING' | 'TARGET_UNREACHABLE'>;
}
export interface CostBudgetApi {
  load(month: string, signal?: AbortSignal): Promise<CostBudgetPayload>;
  preview(month: string, input: CostBudgetInput, signal?: AbortSignal): Promise<CostBudgetPayload>;
  save(month: string, input: CostBudgetInput, expectedRevision: number): Promise<CostBudgetPayload>;
  estimate(input: MenuCostEstimateRequest, signal?: AbortSignal): Promise<MenuCostEstimate>;
}
export const emptyCostBudgetInput = (): CostBudgetInput => ({
  fixed: { reviewed: false, rows: [] }, other: { reviewed: false, rows: [] },
  labor: { reviewed: false, mode: 'UNSET', amountExact: null, profiles: [] },
  forecast: { allocation: 'PER_PORTION', portionsExact: null, salesExact: null, targetMarginPercentExact: null },
});
export const costMonthNow = (timezone: string): string => {
  const parts = new Intl.DateTimeFormat('en-US', {timeZone: timezone,year:'numeric',month:'2-digit'}).formatToParts(new Date());
  return `${parts.find(part=>part.type==='year')!.value}-${parts.find(part=>part.type==='month')!.value}`;
};
export const costMoney = (value: string | null, locale: Locale) => value === null ? '—' : new Intl.NumberFormat(locale === 'es' ? 'es-US' : 'en-US', { style:'currency', currency:'USD' }).format(Number(value));
